import { useCallback, useMemo, useState } from 'react';

import * as z from 'zod';

import type { SearchParamFilters } from '@/_shared';
import { useSearchParams } from '@/_shared';

export type FilterType = {
  name: string;
  value: string[];
};

export const filterState = z.object({
  per_page: z.number().optional().catch(50),
  page: z.number().optional().catch(1),
  sort: z.string().optional().catch(''),
  search_str: z.string().optional().catch(''),
  filter_str: z.string().optional().catch(''),
});

export type APIFilterState = {
  sort: string;
  filter: FilterType[];
  per_page: number;
  page: number;
  search_str: string;
  filter_str: string;
};

const initialValues: Readonly<APIFilterState> = {
  sort: '',
  per_page: 500,
  search_str: '',
  page: 1,
  filter: [],
  filter_str: '',
};

export function useApiController(init?: Partial<APIFilterState>, shouldSyncUrl = true) {
  const [searchParams, setSearchParams] = useSearchParams();

  const controlsURLValues = useMemo(() => {
    const nControls = { ...initialValues, ...(init ?? {}) };
    const { sort, per_page, page, search_str, filter_str } = searchParams;

    if (sort) {
      nControls.sort = sort;
    }
    if (per_page) {
      nControls.per_page = Number(per_page);
    }
    if (page) {
      nControls.page = Number(page);
    }
    if (search_str) {
      nControls.search_str = search_str;
    }

    if (filter_str) {
      nControls.filter_str = filter_str;
      const nFilters: FilterType[] = [];
      const filtersArray = filter_str.split('&');
      filtersArray.forEach((filter: string) => {
        const [name, value] = filter.split('=');
        nFilters.push({ name: name.replace('filter[', '').replace(']', ''), value: value.split(',') });
      });

      nControls.filter = nFilters;
    }

    return nControls;
  }, [init, searchParams]);

  const [controls, setControls] = useState<APIFilterState>(controlsURLValues);

  const { filter, per_page, page, sort, search_str } = controls;

  const updateURLSearchParams = useCallback(
    (currentFilters: APIFilterState) => {
      if (!shouldSyncUrl) return;

      const params: SearchParamFilters = {};

      // Check numeric parameters
      if (currentFilters.per_page !== initialValues.per_page) {
        params.per_page = currentFilters.per_page;
      }

      if (currentFilters.page !== initialValues.page) {
        params.page = currentFilters.page;
      }

      // Check string parameters
      if (currentFilters.sort && currentFilters.sort !== initialValues.sort) {
        params.sort = currentFilters.sort;
      }

      if (currentFilters.search_str && currentFilters.search_str !== initialValues.search_str) {
        params.search_str = currentFilters.search_str;
      }

      if (currentFilters.filter_str && currentFilters.filter_str !== initialValues.filter_str) {
        params.filter_str = currentFilters.filter_str;
      }

      setSearchParams(params);
    },
    [shouldSyncUrl, setSearchParams]
  );

  const filterToFilterStr = useCallback((filters: FilterType[]) => {
    return filters
      .filter((item) => item.value.length > 0)
      .map((item) => `filter[${item.name}]=${item.value.join(',')}`)
      .join('&');
  }, []);

  const setFilters = useCallback(
    (nFilter: FilterType) => {
      if (!nFilter) return;

      const fFilter = filter.filter((item) => item.name !== nFilter.name);
      const allFilters = nFilter.value.length > 0 ? [...fFilter, { ...nFilter }] : [...fFilter];

      const filter_str = filterToFilterStr(allFilters);

      setControls((s) => {
        const newValue = { ...s, filter: allFilters, page: 1, search_str: '' };
        updateURLSearchParams({ ...newValue, filter_str });
        return newValue;
      });
    },
    [filter, filterToFilterStr, updateURLSearchParams]
  );

  const searching = useMemo(() => {
    const setSearch = (str: string) => {
      if (str === search_str) {
        return;
      }

      // reset page to 1 on each search entry
      setControls((state) => {
        const newValue = { ...state, search_str: str, page: 1 };
        updateURLSearchParams(newValue);
        return newValue;
      });
    };
    return { search_str, setSearch };
  }, [search_str, updateURLSearchParams]);

  const sorting = useMemo(() => {
    const setSort = (s: string) => {
      setControls((state) => {
        const newValue = { ...state, sort: s, page: 1 };
        updateURLSearchParams(newValue);
        return newValue;
      });
    };
    return { sort, setSort };
  }, [sort, updateURLSearchParams]);

  const pagination = useMemo(() => {
    const handleNext = (current_page: number, last_page: number) => {
      if (current_page === last_page) return;
      setControls((p) => {
        const newValue = { ...p, page: current_page + 1 };
        updateURLSearchParams(newValue);
        return newValue;
      });
    };

    const handlePrevious = (current_page: number) => {
      if (current_page === 1) return;
      setControls((p) => {
        const newValue = { ...p, page: current_page - 1 };
        updateURLSearchParams(newValue);
        return newValue;
      });
    };

    const handleSetLast = (current_page: number, last_page: number) => {
      if (current_page === last_page) return;
      setControls((p) => {
        const newValue = { ...p, page: last_page };
        updateURLSearchParams(newValue);
        return newValue;
      });
    };

    const handleSetFirst = (current_page: number) => {
      if (current_page === 1) return;

      setControls((p) => {
        const newValue = { ...p, page: 1 };
        updateURLSearchParams(newValue);
        return newValue;
      });
    };

    const handleSetNumberOfPages = (n: number) => {
      setControls((s) => {
        const newValue = { ...s, per_page: n, page: 1 };
        updateURLSearchParams(newValue);
        return newValue;
      });
    };

    return { handleNext, handlePrevious, handleSetLast, handleSetFirst, handleSetNumberOfPages, per_page };
  }, [per_page, updateURLSearchParams]);

  const createAPIParamStr = useCallback(
    ({
      perPage,
      page,
      filters,
      sortStr,
      searchStr,
    }: {
      perPage: number;
      page: number;
      filters: FilterType[];
      sortStr: string;
      searchStr: string;
    }) => {
      const nFilterStr = filterToFilterStr(filters);
      let str = `?per_page=${perPage}&page=${page}`;
      if (nFilterStr) {
        str += `&${nFilterStr}`;
      }
      if (sortStr) {
        str += `&sort=${sortStr}`;
      }
      if (searchStr) {
        str += `&search=${encodeURIComponent(searchStr)}`;
      }
      return str;
    },
    [filterToFilterStr]
  );

  const apiParamStr = useMemo(() => {
    return createAPIParamStr({
      page,
      perPage: per_page,
      filters: filter,
      sortStr: sort,
      searchStr: searching.search_str,
    });
  }, [createAPIParamStr, per_page, page, filter, sort, searching.search_str]);

  const reset = useCallback(() => {
    setControls({ ...initialValues, ...(init ?? {}) });
    if (shouldSyncUrl) {
      setSearchParams({});
    }
  }, [init, shouldSyncUrl, setSearchParams]);

  return {
    page,
    pagination,

    sorting,
    searching,

    filter,
    setFilters,
    filterToFilterStr,

    createAPIParamStr,
    apiParamStr,
    reset,
  };
}

export type UseApiController = ReturnType<typeof useApiController>;
export type PaginationHandlerProps = UseApiController['pagination'];
export type SetFiltersProps = UseApiController['setFilters'];
export type SearchingProps = UseApiController['searching'];
export type SortingProps = UseApiController['sorting'];
export type FilterProps = UseApiController['filter'];
